import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Button,
  useTheme,
  alpha,
  useMediaQuery,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  borderRadius,
  getControlBorder,
  getControlBorderColor,
  getModernButton,
  getModernScrollbar,
  getUnifiedScreenFrame,
  layoutGap,
} from '../../../theme/theme';
import Snackbar from '../feedback/Snackbar';
import SetupRevisionProgress from '../feedback/SetupRevisionProgress';
import {
  useCurrentScreen,
  useNavigationHistory,
  useSelectedOption,
  useSetupRevisionStatus,
} from '../../stores/useUIStore';
import { useConfigurationStore } from '../../stores/useConfigurationStore';
import type { Screen } from '../../types';
import { webLog } from '../../api/grpc/webLogger';
import { getWorkflowProgress } from '../../config/workflowUi';
import { getStepHelpTooltip } from '../../config/wizardTooltips';
import {
  type WorkflowVisualVariant,
  getWorkflowChromePalette,
} from '../workflow/WorkflowStepComponents';
import { getWorkspaceFooterSurfaceSx } from '../workflow/WorkflowWorkspacePrimitives';
import WizardAppChromeBar from '../workflow/WizardAppChromeBar';
import WorkflowWizardStepper from '../workflow/WorkflowWizardStepper';
import MainLayoutTitle from './MainLayoutTitle';
import { useWorkflowStepNavigation } from './useWorkflowStepNavigation';
import { useAuthStore } from '../../stores/useAuthStore';
import { useThemeContext } from '../../providers/ThemeContext';
import { openRuntimeVersionInfo } from '../feedback/RuntimeVersionInfo';
import { useHostRibbonCommands, useHostRibbonOwner } from '../hostRibbon';
import { requestHostWindowClose } from '../../lib/utils/windowSizing';

type HostRibbonWorkflowStepStatus = 'active' | 'complete' | 'mutedComplete' | 'upcoming';

type HostRibbonWorkflowStep = {
  screen: Screen;
  label: string;
  status: HostRibbonWorkflowStepStatus;
  clickable: boolean;
};

type HostRibbonActions = {
  onOpenGuide?: () => void;
  onStartTour?: () => void;
  onOpenSummary?: () => void;
};

const hasHostRibbon = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(window.chrome?.webview);
};

const getViewportSize = () => {
  if (typeof window === 'undefined') {
    return { width: 1440, height: 900 };
  }

  const visualViewport = window.visualViewport;

  return {
    width: Math.round(visualViewport?.width || window.innerWidth || window.screen?.availWidth || 1440),
    height: Math.round(visualViewport?.height || window.innerHeight || window.screen?.availHeight || 900),
  };
};

const useHostViewportSize = () => {
  const [viewportSize, setViewportSize] = useState(getViewportSize);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      const nextSize = getViewportSize();
      setViewportSize((currentSize) =>
        currentSize.width === nextSize.width && currentSize.height === nextSize.height
          ? currentSize
          : nextSize
      );
    };

    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);

  return viewportSize;
};

interface MainLayoutProps {
  title: string;
  children: React.ReactNode;
  ariaLabel?: string;
  subtitle?: string;
  onBack?: () => void;
  onNext?: () => void;
  onSecondaryAction?: () => void;
  showBack?: boolean;
  showNext?: boolean;
  showSecondaryAction?: boolean;
  nextDisabled?: boolean;
  secondaryActionDisabled?: boolean;
  secondaryActionAttention?: boolean;
  nextText?: string;
  secondaryActionText?: string;
  maxWidth?: string | number;
  maxHeight?: string | number;
  disableContentScroll?: boolean;
  /** When true, workflow step clicks are blocked (e.g. during loading/saving). */
  blockStepNavigation?: boolean;
  /** Callback to reinitiate/reset the workflow - rendered at end of workflow steps. */
  onReinitiateWorkflow?: () => void;
  /** Override workflow applied state - if not provided, uses store value. */
  workflowAppliedOverride?: boolean;
  /** Hide utility chrome that would otherwise occupy the unsafe top-right window-controls area. */
  hideUtilityChrome?: boolean;
  /** Use a slimmer workflow stepper treatment for compact setup screens. */
  compactWorkflowChrome?: boolean;
  /** Choose whether this screen uses guided-step or workspace visual chrome. */
  workflowChromeVariant?: WorkflowVisualVariant;
  /** Optional compact header meta rendered above the workflow steps. */
  headerMeta?: React.ReactNode;
  /** Hide runtime badge from top-right utility chrome when it is rendered elsewhere. */
  showRuntimeBadgeInUtilityChrome?: boolean;
  /** Optional persistent footer summary content rendered beside navigation. */
  footerContent?: React.ReactNode;
  /** Optional custom controls rendered in the top-right utility chrome. */
  utilityChrome?: React.ReactNode;
  /** Optional ref to the whole top-right chrome action cluster for onboarding focus. */
  utilityChromeAreaRef?: React.Ref<HTMLDivElement>;
  /** Optional ref to the bottom navigation/action area for onboarding focus. */
  navigationAreaRef?: React.Ref<HTMLDivElement>;
  /** Optional ref to the primary action button cluster for onboarding focus. */
  navigationActionsRef?: React.Ref<HTMLDivElement>;
  /** Optional test id for the primary action button cluster. */
  navigationActionsTestId?: string;
  /** Page-owned actions that should be mirrored into the native host ribbon when available. */
  hostRibbonActions?: HostRibbonActions;
  /** Render the surface as a dedicated workspace shell instead of standard app chrome. */
  windowVariant?: 'app' | 'workspace';
}

const MainLayout: React.FC<MainLayoutProps> = ({
  title,
  children,
  ariaLabel,
  subtitle,
  onBack,
  onNext,
  onSecondaryAction,
  showBack = false,
  showNext = false,
  showSecondaryAction = false,
  nextDisabled = false,
  secondaryActionDisabled = false,
  secondaryActionAttention = false,
  nextText = 'Next',
  secondaryActionText = 'Apply',
  disableContentScroll = false,
  blockStepNavigation = false,
  onReinitiateWorkflow,
  workflowAppliedOverride,
  hideUtilityChrome = false,
  compactWorkflowChrome = false,
  workflowChromeVariant,
  headerMeta,
  showRuntimeBadgeInUtilityChrome = true,
  footerContent,
  utilityChrome,
  utilityChromeAreaRef,
  navigationAreaRef,
  navigationActionsRef,
  navigationActionsTestId,
  hostRibbonActions,
  windowVariant = 'app',
}) => {
  const theme = useTheme();
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const isConstrainedViewport = useMediaQuery('(max-width:1180px), (max-height:720px)');
  const effectiveCompactWorkflowChrome = compactWorkflowChrome
    ? isConstrainedViewport
    : isNarrowViewport;
  const viewportSize = useHostViewportSize();
  const currentScreen = useCurrentScreen();
  const navigationHistory = useNavigationHistory();
  const selectedOption = useSelectedOption();
  const setupRevisionStatus = useSetupRevisionStatus();
  const isWorkflowApplied = useConfigurationStore((state) => state.isWorkflowApplied);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const themeContext = useThemeContext();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const isWorkspaceWindow = windowVariant === 'workspace';
  const hostRibbonAvailable = hasHostRibbon();
  const hostRibbonGuideAction = hostRibbonActions?.onOpenGuide;
  const hostRibbonTourAction = hostRibbonActions?.onStartTour;
  const hostRibbonSummaryAction = hostRibbonActions?.onOpenSummary;
  const secondaryActionNeedsAttention = secondaryActionAttention && !secondaryActionDisabled;
  const secondaryActionAttentionTraceKeyRef = useRef<string>('');
  useEffect(() => {
    if (!showSecondaryAction) {
      return;
    }

    const payload = {
      screen: currentScreen,
      windowVariant,
      secondaryActionText,
      secondaryActionAttention,
      secondaryActionDisabled,
      secondaryActionNeedsAttention,
    };
    const traceKey = JSON.stringify(payload);
    if (secondaryActionAttentionTraceKeyRef.current === traceKey) {
      return;
    }

    secondaryActionAttentionTraceKeyRef.current = traceKey;
    webLog.info('quickFillAttention', `layout-secondary-action-state ${JSON.stringify(payload)}`);
  }, [
    currentScreen,
    secondaryActionAttention,
    secondaryActionDisabled,
    secondaryActionNeedsAttention,
    secondaryActionText,
    showSecondaryAction,
    windowVariant,
  ]);
  const resolvedWorkflowChromeVariant =
    workflowChromeVariant ?? (isWorkspaceWindow ? 'workspace' : 'wizard');
  const workflowPalette = useMemo(
    () => getWorkflowChromePalette(theme, resolvedWorkflowChromeVariant),
    [resolvedWorkflowChromeVariant, theme]
  );
  const workspaceFooterSurfaceSx = useMemo(() => getWorkspaceFooterSurfaceSx(theme), [theme]);
  const screenFrame = useMemo(() => getUnifiedScreenFrame(theme), [theme]);
  const buttonStyles = useMemo(() => getModernButton(theme), [theme]);
  const scrollbarStyles = useMemo(() => getModernScrollbar(theme), [theme]);
  
  const workflowProgress = useMemo(
    () => getWorkflowProgress(currentScreen, selectedOption),
    [currentScreen, selectedOption]
  );
  const muteCompletedWorkflowSteps = currentScreen === 'pcfValidation';
  const stepHelpTooltip = useMemo(
    () => getStepHelpTooltip(currentScreen, selectedOption),
    [currentScreen, selectedOption]
  );
  const isMappingWorkflowStep = currentScreen === 'mappingConfiguration';
  const workflowSelected = selectedOption !== null;
  
  const windowDimensions = viewportSize;

  const handleWorkflowStepClick = useWorkflowStepNavigation({
    steps: workflowProgress.steps,
    currentScreen,
    blocked: blockStepNavigation,
  });

  // Push window dimensions early and again after mount to keep step switches stable.
  useLayoutEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      root.setAttribute('data-window-width', windowDimensions.width.toString());
      root.setAttribute('data-window-height', windowDimensions.height.toString());
    }

    const webview = window.chrome?.webview;
    if (!webview) {
      return undefined;
    }

    const sendResize = () => {
      webview.postMessage(JSON.stringify({
        type: 'resize',
        width: windowDimensions.width,
        height: windowDimensions.height
      }));
    };

    sendResize();

    const frameId = window.requestAnimationFrame(sendResize);
    const timeoutId = window.setTimeout(sendResize, 90);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [currentScreen, title, windowDimensions]);

  // Window control functions for borderless window
  // window.close() is a no-op in the main WebView2 frame — the closeWindow
  // message asks the WPF host to hide the tool window instead.
  const handleClose = () => {
    if (!requestHostWindowClose()) {
      window.close();
    }
  };

  const handleSignOut = useCallback(async () => {
    if (isSigningOut) {
      return;
    }

    setIsSigningOut(true);
    try {
      await logout();
    } finally {
      setIsSigningOut(false);
    }
  }, [isSigningOut, logout]);

  const hostRibbonWorkflowSteps = useMemo<HostRibbonWorkflowStep[]>(() => {
    if (isWorkflowApplied || workflowProgress.steps.length <= 1 || isMappingWorkflowStep) {
      return [];
    }

    const menuIndex = workflowProgress.steps.findIndex((step) => step.screen === 'menu');

    return workflowProgress.steps.map((step, index) => {
      const isActive = index === workflowProgress.activeIndex;
      const isComplete = index < workflowProgress.activeIndex;
      const hasBeenReached = navigationHistory.includes(step.screen);
      const isLockedByWorkflowChoice = menuIndex >= 0 && index > menuIndex && !workflowSelected;
      const clickable = !blockStepNavigation && hasBeenReached && !isLockedByWorkflowChoice && !isActive;
      const status: HostRibbonWorkflowStepStatus = isActive
        ? 'active'
        : isComplete && muteCompletedWorkflowSteps
          ? 'mutedComplete'
          : isComplete
            ? 'complete'
            : 'upcoming';

      return {
        screen: step.screen,
        label: step.label,
        status,
        clickable,
      };
    });
  }, [
    blockStepNavigation,
    isMappingWorkflowStep,
    isWorkflowApplied,
    muteCompletedWorkflowSteps,
    navigationHistory,
    workflowProgress.activeIndex,
    workflowProgress.steps,
    workflowSelected,
  ]);

  // Declared above the effects below so the ownership claim is committed first —
  // React runs a component's effects in hook declaration order.
  const { publish: publishHostRibbon, isCurrentOwner: ownsHostRibbon } = useHostRibbonOwner(
    'workflow-setup',
    hostRibbonAvailable && !isMappingWorkflowStep
  );

  useHostRibbonCommands(
    'workflow-setup',
    hostRibbonAvailable && !isMappingWorkflowStep,
    (payload) => {
      if (!ownsHostRibbon()) {
        return;
      }

      switch (payload.command) {
        case 'setWorkflowStep': {
          const stepIndex = workflowProgress.steps.findIndex((step) => step.screen === payload.value);
          if (stepIndex >= 0 && hostRibbonWorkflowSteps[stepIndex]?.clickable) {
            handleWorkflowStepClick(
              workflowProgress.steps[stepIndex].screen,
              stepIndex,
              'hostRibbonStep'
            );
          }
          return;
        }
        case 'openVersions':
          openRuntimeVersionInfo();
          return;
        case 'toggleTheme':
          themeContext.toggleTheme();
          return;
        case 'openGuide':
          hostRibbonGuideAction?.();
          return;
        case 'startTour':
          hostRibbonTourAction?.();
          return;
        case 'openSummary':
          hostRibbonSummaryAction?.();
          return;
        case 'signOut':
          void handleSignOut();
          return;
        default:
      }
    }
  );

  useEffect(() => {
    if (!hostRibbonAvailable || isMappingWorkflowStep) {
      return;
    }

    publishHostRibbon({
      visible: true,
      showWorkspaceTabs: false,
      showWorkflowSteps: hostRibbonWorkflowSteps.length > 0,
      workflowSteps: hostRibbonWorkflowSteps,
      showProfiles: false,
      showGuide: Boolean(hostRibbonGuideAction),
      showTour: Boolean(hostRibbonTourAction),
      showTheme: true,
      showSummary: Boolean(hostRibbonSummaryAction),
      canSignOut: Boolean(user),
      isSigningOut,
      userName: user?.username ?? null,
    });
  }, [
    currentScreen,
    hostRibbonGuideAction,
    hostRibbonAvailable,
    hostRibbonSummaryAction,
    hostRibbonTourAction,
    hostRibbonWorkflowSteps,
    isMappingWorkflowStep,
    isSigningOut,
    publishHostRibbon,
    user,
  ]);

  const wizardWorkflowStepper = useMemo(() => {
    if (hostRibbonAvailable || isWorkflowApplied || workflowProgress.steps.length <= 1 || isMappingWorkflowStep) {
      return null;
    }

    return (
      <WorkflowWizardStepper
        steps={workflowProgress.steps}
        activeIndex={workflowProgress.activeIndex}
        navigationHistory={navigationHistory}
        selectedOption={selectedOption}
        workflowSelected={workflowSelected}
        compact={effectiveCompactWorkflowChrome}
        muteCompletedWorkflowSteps={muteCompletedWorkflowSteps}
        currentStepLabel={workflowProgress.currentStep.label}
        onStepClick={handleWorkflowStepClick}
        blockNavigation={blockStepNavigation}
        workflowPalette={workflowPalette}
      />
    );
  }, [
    isWorkflowApplied,
    workflowProgress.steps,
    workflowProgress.activeIndex,
    workflowProgress.currentStep.label,
    isMappingWorkflowStep,
    navigationHistory,
    selectedOption,
    workflowSelected,
    effectiveCompactWorkflowChrome,
    muteCompletedWorkflowSteps,
    handleWorkflowStepClick,
    blockStepNavigation,
    hostRibbonAvailable,
    workflowPalette,
  ]);

  /** Show the headline column whenever there is copy, except the dense mapping shell (workspace + compact). */
  const showWizardTitleBlock =
    !hostRibbonAvailable &&
    Boolean(title || subtitle || headerMeta) &&
    (!effectiveCompactWorkflowChrome ||
      resolvedWorkflowChromeVariant === 'wizard' ||
      windowVariant === 'app');
  const showWebChromeBar = !hostRibbonAvailable && !hideUtilityChrome;
  const showBareTitleBlock = !hostRibbonAvailable && hideUtilityChrome && showWizardTitleBlock;

  // Note: Window dragging is handled by WPF drag strip above WebView2
  // WebView2 captures all mouse events, so drag must be outside its bounds

  return (
    <Paper
      component="main"
      aria-label={ariaLabel || `${title} ${isWorkspaceWindow ? 'workspace' : 'guided setup'}`}
      elevation={0}
      data-window-variant={windowVariant}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        pt: isWorkspaceWindow ? 0 : isMappingWorkflowStep ? 0 : 0.125,
        px: isWorkspaceWindow
          ? 0
          : isMappingWorkflowStep
            ? { xs: 0.125, sm: 0.25, md: 0.375 }
            : { xs: 0.25, sm: 0.375, md: 0.5 },
        pb: isWorkspaceWindow ? 0 : isMappingWorkflowStep ? 0 : 0.125,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: screenFrame.backgroundColor,
        border: screenFrame.border,
        borderRadius: screenFrame.borderRadius,
        boxSizing: 'border-box',
        minWidth: 0,
        boxShadow: screenFrame.boxShadow,
      }}
    >
      {showWebChromeBar ? (
        <WizardAppChromeBar
          showTitleBlock={showWizardTitleBlock}
          title={title}
          subtitle={subtitle}
          headerMeta={headerMeta}
          compactTitle={effectiveCompactWorkflowChrome}
          compactChrome={effectiveCompactWorkflowChrome}
          showActiveDocument={!isWorkspaceWindow}
          workflowStepper={wizardWorkflowStepper}
          utilityChrome={utilityChrome}
          rightToolsRef={utilityChromeAreaRef}
          stepHelpTooltip={stepHelpTooltip ?? undefined}
          showRuntimeBadgeInUtilityChrome={showRuntimeBadgeInUtilityChrome}
          user={user ? { username: user.username } : null}
          isSigningOut={isSigningOut}
          onSignOut={handleSignOut}
          onClose={handleClose}
          showWindowTools={true}
          workflowPalette={workflowPalette}
        />
      ) : (
        showBareTitleBlock && (
          <MainLayoutTitle
            title={title}
            subtitle={subtitle}
            headerMeta={headerMeta}
            compact={effectiveCompactWorkflowChrome}
          />
        )
      )}

      {/* Sits above the content area, not inside it: the content box stretches
          every direct child to fill the remaining height. */}
      <SetupRevisionProgress status={setupRevisionStatus} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: disableContentScroll ? 'stretch' : 'center',
          overflowY: disableContentScroll ? 'hidden' : 'auto',
          mb: 0,
          pt: isMappingWorkflowStep
            ? { xs: 0.05, md: 0.12 }
            : effectiveCompactWorkflowChrome
              ? { xs: 0.2, md: 0.35 }
              : { xs: 0.25, md: 0.5, lg: 0.75 },
          pb: isNarrowViewport ? layoutGap.section : isMappingWorkflowStep ? { sm: 0.05, md: 0.12 } : effectiveCompactWorkflowChrome ? { sm: 0.2, md: 0.35 } : { sm: 0.25, md: 0.5, lg: 0.75 },
          px: isMappingWorkflowStep
            ? { xs: 0, sm: 0.15, md: 0.2 }
            : effectiveCompactWorkflowChrome
              ? { xs: 0, sm: 0.25, md: 0.35 }
              : { xs: 0, sm: 0.375, md: 0.5, lg: 0.75 },
          position: 'relative',
          zIndex: 1,
          '& > *': {
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            mx: disableContentScroll ? 0 : 'auto',
          },
          ...(disableContentScroll
            ? {
                '& > *': {
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  flex: 1,
                  minHeight: 0,
                },
              }
            : {}),
          ...scrollbarStyles,
        }}
      >
        {children}
      </Box>

      {/* Navigation buttons - SLIM */}
      {(showBack || showNext || showSecondaryAction) && (
        <Box
          ref={navigationAreaRef}
          sx={[
            {
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            gap: isMappingWorkflowStep ? 0.24 : 0.5,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            width: '100%',
            pt: isMappingWorkflowStep ? 0.06 : effectiveCompactWorkflowChrome ? 0.25 : 0.375,
            pb: isMappingWorkflowStep ? 0 : effectiveCompactWorkflowChrome ? 0 : 0.125,
            position: 'relative',
            zIndex: 1,
            borderTop: `1px solid ${alpha(theme.palette.divider, isMappingWorkflowStep ? 0.05 : resolvedWorkflowChromeVariant === 'workspace' ? 0.1 : 0.08)}`,
            background: isMappingWorkflowStep
              ? alpha(theme.palette.background.default, 0.02)
              : `linear-gradient(180deg, transparent, ${alpha(theme.palette.background.default, resolvedWorkflowChromeVariant === 'workspace' ? 0.12 : 0.15)})`,
          },
            resolvedWorkflowChromeVariant === 'workspace' ? workspaceFooterSurfaceSx : null,
          ]}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: layoutGap.dense,
              minWidth: 0,
              flex: 1,
              flexBasis: { xs: '100%', sm: 0 },
            }}
          >
            {(workflowAppliedOverride ?? isWorkflowApplied) && onReinitiateWorkflow && (
              <Button
                variant="text"
                size="small"
                startIcon={<RefreshIcon sx={{ fontSize: '0.75rem' }} />}
                onClick={onReinitiateWorkflow}
                sx={{
                  fontSize: effectiveCompactWorkflowChrome ? '0.6875rem' : '0.75rem',
                  py: 0.25,
                  px: layoutGap.dense,
                  color: theme.palette.text.secondary,
                  minWidth: 'auto',
                  borderRadius: `${borderRadius.md}px`,
                  border: getControlBorder(theme),
                  backgroundColor: workflowPalette.subtleBackground,
                  '&:hover': {
                    color: theme.palette.warning.light,
                    backgroundColor: alpha(theme.palette.warning.main, 0.06),
                    borderColor: getControlBorderColor(theme),
                  },
                }}
              >
              Revise setup
              </Button>
            )}
            {footerContent && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMappingWorkflowStep ? 0.24 : 0.625,
                  minWidth: 0,
                  overflow: 'hidden',
                  flexWrap: effectiveCompactWorkflowChrome ? 'nowrap' : 'wrap',
                }}
              >
                {footerContent}
              </Box>
            )}
          </Box>
          {showBack && (
            <Button
              data-testid="nav-back"
              variant="outlined"
              onClick={onBack}
              aria-label="Go to previous setup step"
              size="small"
              sx={{
                ...buttonStyles.outlined,
                minWidth: isMappingWorkflowStep ? 50 : effectiveCompactWorkflowChrome ? 64 : 72,
                fontSize: effectiveCompactWorkflowChrome ? '0.6875rem' : '0.75rem',
                py: effectiveCompactWorkflowChrome ? 0.2 : 0.25,
                px: effectiveCompactWorkflowChrome ? 0.6 : 0.75,
              }}
            >
              Back
            </Button>
          )}
          {showNext && (
            <Box
              ref={navigationActionsRef}
              data-testid={navigationActionsTestId}
              sx={{
                display: 'flex',
                gap: isMappingWorkflowStep ? 0.35 : 0.5,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                ml: { xs: 'auto', sm: 0 },
              }}
            >
              {showSecondaryAction && (
                <Button
                  variant="outlined"
                  data-testid="nav-secondary"
                  onClick={onSecondaryAction}
                  disabled={secondaryActionDisabled}
                  data-attention={secondaryActionNeedsAttention ? 'true' : undefined}
                  aria-label={secondaryActionDisabled ? `${secondaryActionText} unavailable` : secondaryActionText}
                  size="small"
                    sx={{
                      ...buttonStyles.outlined,
                      ...(secondaryActionNeedsAttention
                        ? {
                            borderColor: alpha(theme.palette.primary.main, 0.78),
                            color: theme.palette.primary.light,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.18)}, 0 0 14px ${alpha(theme.palette.primary.main, 0.34)}`,
                            '&:hover': {
                              borderColor: theme.palette.primary.light,
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.24)}, 0 0 18px ${alpha(theme.palette.primary.main, 0.42)}`,
                            },
                          }
                        : {}),
                      minWidth: isMappingWorkflowStep ? 64 : effectiveCompactWorkflowChrome ? 68 : 72,
                      fontSize: effectiveCompactWorkflowChrome ? '0.6875rem' : '0.75rem',
                      py: effectiveCompactWorkflowChrome ? 0.2 : 0.25,
                      px: effectiveCompactWorkflowChrome ? 0.6 : 0.75,
                      '&:disabled': { color: theme.palette.grey[500], borderColor: theme.palette.grey[700], cursor: 'not-allowed' },
                    }}
                  >
                  {secondaryActionText}
                </Button>
              )}
              <Button
                data-testid="nav-next"
                variant="contained"
                onClick={onNext}
                disabled={nextDisabled}
                aria-label={nextDisabled ? `${nextText} unavailable` : nextText}
                size="small"
                  sx={{
                    ...buttonStyles.contained,
                    minWidth: isMappingWorkflowStep ? 82 : effectiveCompactWorkflowChrome ? 84 : 90,
                    fontSize: effectiveCompactWorkflowChrome ? '0.6875rem' : '0.75rem',
                    py: effectiveCompactWorkflowChrome ? 0.2 : 0.25,
                    px: effectiveCompactWorkflowChrome ? 0.7 : 0.875,
                    '&:disabled': { backgroundColor: theme.palette.grey[800], color: theme.palette.grey[500], cursor: 'not-allowed', transform: 'none', boxShadow: 'none' },
                  }}
                >
                {nextText}
              </Button>
            </Box>
          )}
        </Box>
      )}
      <Snackbar />
    </Paper>
  );
};

export default MainLayout;
